import { useEffect, useCallback, useMemo } from 'react';

import { useAppStore } from '@/store';
import { useLibraryTabsQuery } from './queries/useLibraryTabsQuery';
import { useLibraryQuery } from './queries/useLibraryQuery';
import { useLibraryItemInfoMutation } from './queries/useLibraryItemInfoMutation';
import { BREAKPOINTS } from '@/consts/breakpoints';

import type { IAppStore } from '@/store';

const selector = (store: IAppStore) => ({
  activeTabSlug: store.activeTabSlug,
  setActiveTabSlug: store.setActiveTabSlug,
  selectedPreset: store.selectedPreset,
  hasAutoSelectedPreset: store.hasAutoSelectedPreset,
  setHasAutoSelectedPreset: store.setHasAutoSelectedPreset,
  isPresetLoading: store.isPresetLoading,
  setSelectedPreset: store.setSelectedPreset,
  setInputs: store.setInputs,
});

const useLibrary = () => {
  const {
    activeTabSlug,
    setActiveTabSlug,
    selectedPreset,
    hasAutoSelectedPreset,
    setHasAutoSelectedPreset,
  } = useAppStore(selector);

  const tabsQuery = useLibraryTabsQuery();
  const libraryQuery = useLibraryQuery({ tagId: activeTabSlug ?? undefined });
  const presetMutation = useLibraryItemInfoMutation();

  const tabs = useMemo(() => tabsQuery.data ?? [], [tabsQuery.data]);
  const items = useMemo(() => libraryQuery.data ?? [], [libraryQuery.data]);
  const isLoading = tabsQuery.isLoading || libraryQuery.isLoading;

  // Set first tab as active when tabs are loaded
  useEffect(() => {
    if (tabs.length > 0 && !activeTabSlug) {
      setActiveTabSlug(tabs[0].slug);
    }
  }, [tabs, activeTabSlug, setActiveTabSlug]);

  // Auto-select first preset on desktop only (mobile shows gallery first)
  // Uses store flag instead of ref so state persists across component remounts
  // Note: usePresetDeeplink sets hasAutoSelectedPreset early to prevent race conditions
  useEffect(() => {
    // Only auto-select on desktop - mobile users should see gallery first
    const isDesktop = window.innerWidth >= BREAKPOINTS.desktop;

    if (
      isDesktop &&
      !hasAutoSelectedPreset &&
      items.length > 0 &&
      !selectedPreset &&
      !isLoading
    ) {
      setHasAutoSelectedPreset(true);
      const firstItem = items[0];
      if (firstItem?.slug) {
        presetMutation.mutate(firstItem.slug);
      }
    }
  }, [
    items,
    selectedPreset,
    isLoading,
    presetMutation,
    hasAutoSelectedPreset,
    setHasAutoSelectedPreset,
  ]);

  const selectTab = useCallback(
    (slug: string) => {
      setActiveTabSlug(slug);
    },
    [setActiveTabSlug]
  );

  return {
    tabs,
    activeTabSlug,
    items,
    isLoading,
    selectTab,
  };
};

const usePreset = () => {
  const { selectedPreset, isPresetLoading, setSelectedPreset, setInputs } =
    useAppStore(selector);

  const presetMutation = useLibraryItemInfoMutation();

  const loadPreset = useCallback(
    (slug: string) => {
      presetMutation.mutate(slug);
    },
    [presetMutation]
  );

  const clearPreset = useCallback(() => {
    setSelectedPreset(null);
    setInputs([]);
  }, [setSelectedPreset, setInputs]);

  return {
    preset: selectedPreset,
    isLoading: isPresetLoading || presetMutation.isPending,
    loadPreset,
    clearPreset,
  };
};

export { useLibrary, usePreset };
